import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Text,
  Dropdown,
  IDropdownOption,
  DatePicker,
  IconButton,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import { DataGrid, DataGridColumn } from "@/ui-controls/DataGrid";
import ShimmerList from "@/ui-controls/ShimmerList";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import UsageChart from "@/components/integrations/usage/UsageChart";
import {
  IntegrationDto,
  IntegrationUsageSummaryDto,
  listIntegrations,
  getUsageSummary,
} from "@/api/integrations";

type ViewMode = "grid" | "chart";

const getStartOfMonth = (): Date => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const getEndOfToday = (): Date => {
  return new Date();
};

const MAX_RANGE_DAYS = 366;

const IntegrationUsagePage = () => {
  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    number | string
  >("all");
  const [customerIdFilter, setCustomerIdFilter] = useState<number | undefined>();
  const [dateFrom, setDateFrom] = useState<Date>(getStartOfMonth());
  const [dateTo, setDateTo] = useState<Date>(getEndOfToday());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<IntegrationUsageSummaryDto[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  useEffect(() => {
    listIntegrations().then((data) => {
      setIntegrations(data?.result || []);
    });
  }, []);

  const validateDates = useCallback(
    (from: Date, to: Date): string | null => {
      if (from > to) return "Date From cannot be greater than Date To.";
      const diffDays =
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays > MAX_RANGE_DAYS)
        return "Date range cannot exceed 1 year.";
      return null;
    },
    []
  );

  const handleDateFromChange = (date: Date | null | undefined) => {
    if (!date) return;
    setDateFrom(date);
    setValidationError(validateDates(date, dateTo));
  };

  const handleDateToChange = (date: Date | null | undefined) => {
    if (!date) return;
    setDateTo(date);
    setValidationError(validateDates(dateFrom, date));
  };

  useEffect(() => {
    if (validationError) return;

    setLoading(true);

    const fromUtc = new Date(
      dateFrom.getFullYear(),
      dateFrom.getMonth(),
      dateFrom.getDate(),
      0, 0, 0, 0
    ).toISOString();

    const toUtc = new Date(
      dateTo.getFullYear(),
      dateTo.getMonth(),
      dateTo.getDate(),
      23, 59, 59, 999
    ).toISOString();

    const integrationId =
      selectedIntegrationId === "all"
        ? undefined
        : (selectedIntegrationId as number);

    getUsageSummary({
      dateFrom: fromUtc,
      dateTo: toUtc,
      integrationId,
      customerId: customerIdFilter,
    })
      .then((data) => setSummaryData(data || []))
      .catch(() => setSummaryData([]))
      .finally(() => setLoading(false));
  }, [selectedIntegrationId, customerIdFilter, dateFrom, dateTo, validationError, refreshTrigger]);

  const integrationOptions: IDropdownOption[] = [
    { key: "all", text: "All Integrations" },
    ...integrations.map((i) => ({
      key: i.id,
      text: `${i.code} - ${i.name}`,
    })),
  ];

  const formatUsageType = (usageType: string): string => {
    // Remove trailing "Op" suffix
    let name = usageType.replace(/Op$/, "");
    // Insert spaces before uppercase letters (PascalCase → words)
    name = name.replace(/([a-z])([A-Z])/g, "$1 $2");
    // Capitalize first letter, lowercase the rest of each word
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const getIntegrationName = (integrationId: number): string => {
    const integration = integrations.find((i) => i.id === integrationId);
    return integration ? `${integration.code}` : integrationId.toString();
  };

  const columns: DataGridColumn[] = [
    {
      key: "integrationId",
      name: "Integration",
      minWidth: 120,
      maxWidth: 180,
      onRender: (item: IntegrationUsageSummaryDto) =>
        getIntegrationName(item.integrationId),
    },
    {
      key: "customerId",
      name: "Customer",
      fieldName: "customerId",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "usageType",
      name: "Usage Type",
      minWidth: 140,
      maxWidth: 200,
      onRender: (item: IntegrationUsageSummaryDto) =>
        formatUsageType(item.usageType),
    },
    {
      key: "totalUnits",
      name: "Total Units",
      fieldName: "totalUnits",
      minWidth: 100,
      maxWidth: 120,
    },
    {
      key: "usedMasterConfig",
      name: "Master Config",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: IntegrationUsageSummaryDto) =>
        item.usedMasterConfig ? "Yes" : "No",
    },
    {
      key: "estimatedCost",
      name: "Est. Cost",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: IntegrationUsageSummaryDto) =>
        item.estimatedCost != null
          ? `${item.currencyCode ?? ""} ${item.estimatedCost.toFixed(2)}`
          : "-",
    },
    {
      key: "earliestOccurrence",
      name: "First Usage",
      minWidth: 150,
      maxWidth: 180,
      onRender: (item: IntegrationUsageSummaryDto) =>
        new Date(item.earliestOccurrence).toLocaleString(),
    },
    {
      key: "latestOccurrence",
      name: "Last Usage",
      minWidth: 150,
      maxWidth: 180,
      onRender: (item: IntegrationUsageSummaryDto) =>
        new Date(item.latestOccurrence).toLocaleString(),
    },
  ];

  // Compute max selectable date for DateFrom (dateTo or today)
  const maxDateFrom = dateTo < new Date() ? dateTo : new Date();
  // Compute min selectable date for DateTo
  const minDateTo = dateFrom;
  // Compute max selectable date for DateTo (1 year from dateFrom or today, whichever is earlier)
  const maxRangeEnd = new Date(dateFrom);
  maxRangeEnd.setFullYear(maxRangeEnd.getFullYear() + 1);
  const today = new Date();
  const maxDateTo = maxRangeEnd < today ? maxRangeEnd : today;

  return (
    <Stack tokens={{ childrenGap: 8 }} styles={{ root: { padding: "16px" } }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        Integration Usage
      </Text>

      <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="end">
        <Dropdown
          label="Integration"
          options={integrationOptions}
          selectedKey={selectedIntegrationId}
          onChange={(_, option) =>
            setSelectedIntegrationId(option?.key as number | string)
          }
          styles={{ dropdown: { width: 300 } }}
        />
        <CustomerComboBox
          selectedCustomerId={customerIdFilter}
          onChange={(id) => setCustomerIdFilter(id)}
          placeholder="Filter by customer..."
        />
        <DatePicker
          label="Date From"
          value={dateFrom}
          onSelectDate={handleDateFromChange}
          maxDate={maxDateFrom}
          styles={{
            root: { width: 160 },
            statusMessage: { display: "none" },
          }}
        />
        <DatePicker
          label="Date To"
          value={dateTo}
          onSelectDate={handleDateToChange}
          minDate={minDateTo}
          maxDate={maxDateTo}
          styles={{
            root: { width: 160 },
            statusMessage: { display: "none" },
          }}
        />
        <IconButton
          iconProps={{ iconName: "Refresh" }}
          title="Refresh"
          onClick={() => setRefreshTrigger((n) => n + 1)}
          disabled={loading}
        />
        <IconButton
          iconProps={{ iconName: "GridViewMedium" }}
          title="Grid view"
          onClick={() => setViewMode("grid")}
          checked={viewMode === "grid"}
          styles={{
            root: {
              backgroundColor: viewMode === "grid" ? "#edebe9" : "transparent",
            },
          }}
        />
        <IconButton
          iconProps={{ iconName: "BarChartHorizontal" }}
          title="Chart view"
          onClick={() => setViewMode("chart")}
          checked={viewMode === "chart"}
          styles={{
            root: {
              backgroundColor: viewMode === "chart" ? "#edebe9" : "transparent",
            },
          }}
        />
      </Stack>

      {validationError && (
        <MessageBar messageBarType={MessageBarType.error}>
          {validationError}
        </MessageBar>
      )}

      {loading ? (
        <ShimmerList numberOfColumns={8} />
      ) : viewMode === "grid" ? (
        <DataGrid items={summaryData} columns={columns} />
      ) : (
        <UsageChart
          data={summaryData}
          getIntegrationName={getIntegrationName}
        />
      )}
    </Stack>
  );
};

export default IntegrationUsagePage;
